import React, { useEffect, useCallback, useRef } from "react";
import { Container } from "./styles";
import InfoLateral from "./InfoLateral";
import InfoPrincipal from "./InfoPrincipal";
import { IProposta } from "../../../utility/IProposta";
import { useEdicaoDemanda } from "../../../hooks/useEdicaoDemanda";
import AbaErro from "../Common/AbaErro";
import AbaAcessoNegado from "../Common/AbaAcessoNegado";
import AbaPrincipalVazio from "./AbaPrincipalVazio";

interface AbaPrincipalProps {
    abaAtiva: string;
    demanda?: IProposta | null;
    loading: boolean;
    error?: boolean;
    acessoNegado?: boolean;
    onRetry?: () => void;
    onEditModeChange?: (isEditing: boolean) => void;
    onIniciarEdicaoRef?: (fn: () => void) => void;
    onCancelarEdicaoRef?: (fn: () => void) => void;
    onSalvarEdicaoRef?: (fn: () => void) => void;
    onHasChangesRef?: (fn: () => boolean) => void;
    onIsValidatingRef?: (fn: () => boolean) => void;
    onIsSavingRef?: (fn: () => boolean) => void;
    onHasErrorsRef?: (fn: () => boolean) => void;
    onRecarregarDemanda?: () => void;
}

export default function AbaPrincipal({ abaAtiva, demanda, loading, error, acessoNegado, onRetry, onEditModeChange, onIniciarEdicaoRef, onCancelarEdicaoRef, onSalvarEdicaoRef, onHasChangesRef, onIsValidatingRef, onIsSavingRef, onHasErrorsRef, onRecarregarDemanda }: AbaPrincipalProps) {
    
    const handleSaveSuccess = useCallback((updatedDemanda: IProposta) => {
        if (onRecarregarDemanda) {
            onRecarregarDemanda();
        }
    }, [onRecarregarDemanda]);
    
    const { 
        isEditing, 
        dadosEditados, 
        iniciarEdicao, 
        cancelarEdicao, 
        salvarEdicao, 
        hasChanges,
        atualizarDadosEditados,
        validateField,
        isValidating,
        errors,
        isSaving,
        camposEditaveisRestritos
    } = useEdicaoDemanda(demanda, handleSaveSuccess);


    const handleIniciarEdicao = useCallback(() => {
        iniciarEdicao();
    }, [iniciarEdicao]);

    const handleCancelarEdicao = useCallback(() => {
        cancelarEdicao();
    }, [cancelarEdicao]);

    const handleSalvarEdicao = useCallback(async () => {
        try {
            await salvarEdicao();
        } catch (error) {
            console.error('Erro ao salvar edição:', error);
        }
    }, [salvarEdicao]);

    useEffect(() => {
        if (onEditModeChange) {
            onEditModeChange(isEditing);
        }
    }, [isEditing, onEditModeChange]);

    const hasErrors = useCallback((): boolean => {
        return Object.keys(errors).length > 0;
    }, [errors]);

    useEffect(() => {
        if (abaAtiva === 'principal') {
            if (onIniciarEdicaoRef) {
                onIniciarEdicaoRef(handleIniciarEdicao);
            }
            if (onCancelarEdicaoRef) {
                onCancelarEdicaoRef(handleCancelarEdicao);
            }
            if (onSalvarEdicaoRef) {
                onSalvarEdicaoRef(handleSalvarEdicao);
            }
            if (onHasChangesRef) {
                onHasChangesRef(hasChanges);
            }
            if (onIsValidatingRef) {
                onIsValidatingRef(() => isValidating);
            }
            if (onIsSavingRef) {
                onIsSavingRef(() => isSaving);
            }
            if (onHasErrorsRef) {
                onHasErrorsRef(hasErrors);
            }
        }
    }, [onIniciarEdicaoRef, onCancelarEdicaoRef, onSalvarEdicaoRef, onHasChangesRef, onIsValidatingRef, onIsSavingRef, onHasErrorsRef, handleIniciarEdicao, handleCancelarEdicao, handleSalvarEdicao, hasChanges, isValidating, isSaving, hasErrors, abaAtiva]);

    const previousAbaAtiva = useRef<string>('');
    
    useEffect(() => {
        if (abaAtiva === 'principal' && previousAbaAtiva.current !== 'principal' && previousAbaAtiva.current !== '') {
            if (onRecarregarDemanda && !isEditing) {
                onRecarregarDemanda();
            }
        }
        previousAbaAtiva.current = abaAtiva;
    }, [abaAtiva, onRecarregarDemanda, isEditing]);

    if (abaAtiva !== 'principal') {
        return null;
    }

    if (acessoNegado) {
        return <AbaAcessoNegado />;
    }

    if (error && onRetry) {
        return <AbaErro onRetry={onRetry} />;
    }

    if (!demanda && !loading) {
        return <AbaPrincipalVazio />;
    }

    const demandaExibida = isEditing ? dadosEditados : demanda;

    return (
        <>
            <Container>
                <InfoLateral 
                    demanda={demandaExibida} 
                    loading={loading} 
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onChange={atualizarDadosEditados}
                    onBlur={validateField}
                    errors={errors}
                    camposEditaveisRestritos={camposEditaveisRestritos}
                />
                <InfoPrincipal 
                    demanda={demandaExibida} 
                    loading={loading} 
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onChange={atualizarDadosEditados}
                    onBlur={validateField}
                    errors={errors}
                    onRecarregarDemanda={onRecarregarDemanda}
                    camposEditaveisRestritos={camposEditaveisRestritos}
                />
            </Container>
        </>
    );
}
